/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback } from "react";
import { fetchDashboard, refreshDashboard } from "@/services/housien/dashboard";
import type {
  DashboardResponse, ComparisonPeriod, DashboardScope,
} from "@/services/housien/dashboard";

interface UseDashboardResult {
  data: DashboardResponse | null;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
  /** Recompute server-side, then replace what is on screen. */
  refresh: () => Promise<void>;
  isRefreshing: boolean;
}

/**
 * Fetches and manages dashboard data for an agency and comparison period,
 * exposing loading and error flags and refetching whenever the agency or
 * comparison changes. Starts in the loading state.
 *
 * @param agencyId - Active agency ID; no fetch occurs while undefined.
 * @param comparison - Optional comparison period passed to the fetch.
 * @param scope - `"me"` for the signed-in member's own numbers.
 * @returns The dashboard data, `isLoading`/`isError` flags, and a `retry` callback.
 */
export function useDashboard(
  agencyId: string | undefined,
  comparison?: ComparisonPeriod,
  scope?: DashboardScope,
): UseDashboardResult {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!agencyId) return;
    setIsLoading(true);
    setIsError(false);
    try {
      const result = await fetchDashboard(agencyId, comparison, scope);
      setData(result);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [agencyId, comparison, scope]);

  /**
   * Ask the server to recompute, and show what it returns.
   *
   * Recompute, then read. The recompute is what the button is for -- the
   * payload is materialized, so a plain re-fetch returns the same copy --
   * but its response is always the agency's, whatever the caller is
   * looking at, so the read that follows is what keeps a member's screen
   * from being handed agency numbers.
   *
   * The read goes straight to the fetch rather than through `load`, which
   * would raise `isLoading` and blank the panel: redrawing the same
   * figures through an empty state reads as a fault.
   */
  const refresh = useCallback(async () => {
    if (!agencyId) return;
    setIsRefreshing(true);
    try {
      await refreshDashboard(agencyId);
      setData(await fetchDashboard(agencyId, comparison, scope));
      setIsError(false);
    } catch {
      setIsError(true);
    } finally {
      setIsRefreshing(false);
    }
  }, [agencyId, comparison, scope]);

  useEffect(() => { load(); }, [load]);

  return { data, isLoading, isError, retry: load, refresh, isRefreshing };
}
